import { formatLocalDateTime } from "utils/dateTime";
import { getCachedCaseDisplayName, cacheCaseDisplayName } from "utils/caseDisplayNameCache";
import {
  isUuidLike,
  resolveCaseNameFromApiFields,
  type CaseItem,
} from "../../../../services/cases.service";

export { cacheCaseDisplayName };
export {
  syncCaseAfterMutation,
  syncCaseStatusAfterMutation,
  removeCaseFromStore,
  patchFromCaseStatusAction,
} from "utils/caseMutationSync";

export function normalizeCaseState(state?: string): string {
  return (state ?? "").toUpperCase().trim().replace(/_/g, "-").replace(/\s+/g, "-");
}

export function getClientStateBadgeProps(state?: string): {
  color: "success" | "warning" | "brand";
  label: string;
} {
  const normalized = String(state ?? "")
    .trim()
    .toUpperCase();
  switch (normalized) {
    case "REGISTERED":
    case "REGISTER":
      return { color: "success", label: "Registered" };
    case "INVITED":
      return { color: "warning", label: "Invited" };
    case "CREATED":
      return { color: "brand", label: "Created" };
    default:
      if (!normalized) return { color: "brand", label: "Created" };
      return {
        color: "brand",
        label: normalized.charAt(0) + normalized.slice(1).toLowerCase(),
      };
  }
}

export function showTaxPayerNameRow(selfAssessment: unknown): boolean {
  if (selfAssessment === false) return true;
  if (selfAssessment === true) return false;
  if (typeof selfAssessment === "string") {
    const t = selfAssessment.trim().toLowerCase();
    return t === "false" || t === "0";
  }
  return false;
}

function isDraftFlagTruthy(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

export function getCaseStatusForUi(caseItem: CaseItem): string | undefined {
  if (isDraftFlagTruthy(caseItem.is_draft)) return "DRAFT";
  return caseItem.case_curr_state;
}

export function isDraftCase(caseItem: CaseItem): boolean {
  const state = normalizeCaseState(getCaseStatusForUi(caseItem));
  return state === "DRAFT" || state === "INITIATED";
}

export function canUserEditCaseName(isLoggedInClient: boolean, caseCreatedBy: unknown): boolean {
  if (isLoggedInClient) return true;
  return (
    String(caseCreatedBy ?? "")
      .trim()
      .toLowerCase() === "client"
  );
}

export function canBusinessUserEditCaseNameInStatus(normalizedState: string): boolean {
  const s = normalizeCaseState(normalizedState);
  return s === "CREATED" || s === "DRAFT" || s === "INITIATED";
}

export function canClientEditCaseNameAndDescription(normalizedState: string): boolean {
  const s = normalizeCaseState(normalizedState);
  return s !== "DRAFT" && s !== "INITIATED";
}

export function getClientProfileHref(
  clientId: string | number | null | undefined
): string | undefined {
  if (clientId === undefined || clientId === null) return undefined;
  const s = String(clientId).trim();
  return s ? `/clients/${s}` : undefined;
}

export function getCaseClientDetailHref(caseItem: CaseItem): string | undefined {
  return getClientProfileHref(caseItem.client_id);
}

export function getCaseNamePrimaryLabel(caseItem: {
  name?: string;
  case_name?: string;
  case_title?: string;
  title?: string;
  case_id_exp?: string;
}): string {
  return getCaseDisplayName(caseItem);
}

export function getCaseNameReferenceSubtitle(caseItem: {
  name?: string;
  case_id_exp?: string;
  description?: string;
}): string | undefined {
  const ref = (caseItem.case_id_exp ?? "").trim();
  if (!ref) return undefined;
  return ref !== getCaseNamePrimaryLabel(caseItem) ? ref : undefined;
}

export function getCaseDisplayName(caseItem: {
  name?: string;
  case_name?: string;
  case_title?: string;
  title?: string;
  case_id_exp?: string;
}): string {
  const label = resolveCaseNameFromApiFields(caseItem);
  return label || "—";
}

export function getCaseDetailPageDisplayName(
  detail:
    | {
        case_id?: string | number;
        name?: string;
        case_name?: string;
        case_title?: string;
        title?: string;
        case_id_exp?: string;
      }
    | null
    | undefined,
  fallbackFromNavigation?: string,
  listCase?: {
    case_id?: string | number;
    name?: string;
    case_name?: string;
    case_title?: string;
    title?: string;
    case_id_exp?: string;
  } | null
): string {
  const caseId = detail?.case_id ?? listCase?.case_id;
  if (caseId !== undefined && caseId !== null) {
    const cached = getCachedCaseDisplayName(caseId);
    if (cached) return cached;
  }

  const candidates = [
    resolveCaseNameFromApiFields(detail ?? {}),
    resolveCaseNameFromApiFields(listCase ?? {}),
    String(fallbackFromNavigation ?? "").trim(),
  ];

  for (const label of candidates) {
    if (label && !isUuidLike(label)) return label;
  }
  for (const label of candidates) {
    if (label) return label;
  }
  return "Case Details";
}

export function getCaseTitleForNavigation(caseItem: {
  case_id?: string | number;
  name?: string;
  case_name?: string;
  case_title?: string;
  title?: string;
  case_id_exp?: string;
}): string {
  if (caseItem.case_id !== undefined && caseItem.case_id !== null) {
    const cached = getCachedCaseDisplayName(caseItem.case_id);
    if (cached) return cached;
  }
  const fromNameFields = resolveCaseNameFromApiFields(caseItem);
  if (fromNameFields && !isUuidLike(fromNameFields)) return fromNameFields;
  return String(caseItem.case_id_exp ?? "").trim();
}

export function filterCasesBySearch(cases: CaseItem[], searchQuery: string): CaseItem[] {
  if (!searchQuery.trim()) {
    return cases;
  }
  const query = searchQuery.toLowerCase();
  const queryPan = searchQuery.toLowerCase().replace(/\s/g, "");
  return cases.filter((c) => {
    const caseName = getCaseDisplayName(c).toLowerCase();
    const description = (c.description ?? "").toLowerCase();
    const caseRef = (c.case_id_exp ?? "").toLowerCase().trim();
    const process = c.process?.toLowerCase() || "";
    const service = c.service?.toLowerCase() || "";
    const clientName = c.client_name?.toLowerCase() || "";
    const assesseeName = c.for_assesse_name?.toLowerCase() || "";
    const assesseePan = (c.for_assesse_pan ?? "").toLowerCase().replace(/\s/g, "");
    const clientPan = (c.client_pan ?? "").toLowerCase().replace(/\s/g, "");
    const state = getCaseStatusForUi(c)?.toLowerCase() || "";
    const author = c.author_name?.toLowerCase() || "";
    const owner = (c.owner as { owner?: string } | null | undefined)?.owner?.toLowerCase() || "";
    return (
      caseName.includes(query) ||
      description.includes(query) ||
      (caseRef && caseRef.includes(query)) ||
      process.includes(query) ||
      service.includes(query) ||
      clientName.includes(query) ||
      assesseeName.includes(query) ||
      (queryPan !== "" && assesseePan !== "" && assesseePan.includes(queryPan)) ||
      (queryPan !== "" && clientPan !== "" && clientPan.includes(queryPan)) ||
      state.includes(query) ||
      author.includes(query) ||
      owner.includes(query)
    );
  });
}

export const CASE_LIST_DATE_FORMAT = "DD/MMM/YYYY hh:mm A";

export function formatCaseDate(
  dateStr: string | undefined,
  format: string = CASE_LIST_DATE_FORMAT
): string {
  return formatLocalDateTime(dateStr, { format, emptyValue: "—", invalidValue: dateStr ?? "—" });
}

function caseAlertMessageText(item: unknown): string {
  if (typeof item === "string") return item.trim();
  if (item && typeof item === "object" && "message" in item) {
    return String((item as { message?: unknown }).message ?? "").trim();
  }
  return "";
}

export function parseCaseAlertMessages(raw: unknown): string[] {
  if (raw === null) return [];
  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? [t] : [];
  }
  if (Array.isArray(raw)) {
    return raw.map(caseAlertMessageText).filter(Boolean);
  }
  if (typeof raw === "object") {
    const t = caseAlertMessageText(raw);
    return t ? [t] : [];
  }
  return [];
}

export function formatEditCaseAlertBanner(messages: string[]): string | null {
  if (messages.length === 0) return null;
  return `Case reverted to draft state: ${messages.join(" ")}`;
}
